import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';

const LoginModal = () => {
  const { loginModalOpen, closeLoginModal, login, loginByCode, register, registerByCode, sendCode, loginModalMode } = useAuth();
  // 一级视图：'login' | 'register'
  const [view, setView] = useState(loginModalMode || 'login');
  // 登录模式：'password' | 'code'
  const [loginMode, setLoginMode] = useState('password');
  // 注册模式：'username' | 'code'
  const [regMode, setRegMode] = useState('username');

  // 登录表单
  const [account, setAccount] = useState(''); // 用户名 / 手机号或邮箱
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  // 注册表单
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regContact, setRegContact] = useState('');
  const [regCode, setRegCode] = useState('');

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // 弹框打开时按入口模式切换到对应视图（登录按钮→登录，注册按钮→注册）
  useEffect(() => {
    if (loginModalOpen) {
      setView(loginModalMode);
      setError('');
      setNotice('');
    }
  }, [loginModalOpen, loginModalMode]);

  if (!loginModalOpen) return null;

  const startCountdown = () => {
    setCountdown(60);
    timerRef.current && clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 发送验证码（登录 / 注册共用）
  const handleSendCode = async () => {
    const contact = view === 'login' ? account : regContact;
    if (!contact.trim()) {
      setError('请先输入手机号或邮箱');
      return;
    }
    if (!contact.includes('@') && !/^1\d{10}$/.test(contact)) {
      setError('请输入正确的手机号或邮箱');
      return;
    }
    setError('');
    setNotice('');
    setIsLoading(true);
    try {
      const demoCode = await sendCode(contact.trim());
      startCountdown();
      // 演示模式：未接短信/邮件网关，直接展示验证码
      setNotice(`【演示模式】验证码已发送：${demoCode}（5 分钟内有效）`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 登录
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (loginMode === 'password') {
      if (!account.trim() || !password.trim()) {
        setError('请填写完整的用户名和密码');
        return;
      }
      if (password.length < 6) {
        setError('密码长度不能少于 6 位');
        return;
      }
    } else {
      if (!account.trim()) {
        setError('请输入手机号或邮箱');
        return;
      }
      if (!code.trim() || code.length < 4) {
        setError('请输入有效的验证码');
        return;
      }
    }

    setIsLoading(true);
    try {
      if (loginMode === 'password') {
        await login(account.trim(), password);
      } else {
        await loginByCode(account.trim(), code.trim());
      }
      resetAll();
    } catch (err) {
      setError(err.message || '登录失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 注册
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (regMode === 'username') {
      if (!regUsername.trim() || !regPassword.trim()) {
        setError('请填写完整的用户名和密码');
        return;
      }
      if (regPassword.length < 6) {
        setError('密码长度不能少于 6 位');
        return;
      }
      if (regPassword !== regConfirm) {
        setError('两次输入的密码不一致');
        return;
      }
    } else {
      if (!regUsername.trim()) {
        setError('请填写用户名');
        return;
      }
      if (!regContact.trim()) {
        setError('请填写手机号或邮箱');
        return;
      }
      if (!regCode.trim() || regCode.length !== 6) {
        setError('请输入 6 位验证码');
        return;
      }
    }

    setIsLoading(true);
    try {
      if (regMode === 'username') {
        await register(regUsername.trim(), regPassword);
      } else {
        await registerByCode(regUsername.trim(), regContact.trim(), regCode.trim());
      }
      resetAll();
    } catch (err) {
      setError(err.message || '注册失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const resetAll = () => {
    setAccount('');
    setPassword('');
    setCode('');
    setRegUsername('');
    setRegPassword('');
    setRegConfirm('');
    setRegContact('');
    setRegCode('');
    setError('');
    setNotice('');
    setView('login');
    setLoginMode('password');
    setRegMode('username');
  };

  const switchView = (v) => {
    setView(v);
    setError('');
    setNotice('');
    setPassword('');
    setCode('');
    setRegPassword('');
    setRegConfirm('');
    setRegCode('');
  };

  const switchMode = (mode) => {
    setLoginMode(mode);
    setError('');
    setNotice('');
    setPassword('');
    setCode('');
  };

  const switchRegMode = (mode) => {
    setRegMode(mode);
    setError('');
    setNotice('');
    setRegPassword('');
    setRegConfirm('');
    setRegCode('');
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) closeLoginModal();
  };

  const inputStyle = { ...styles.input, ...(isLoading ? { opacity: 0.7 } : {}) };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal}>
        <button style={styles.closeBtn} onClick={closeLoginModal}>×</button>
        <h2 style={styles.title}>{view === 'login' ? '登录' : '注册'}</h2>

        {/* 登录 / 注册 切换 */}
        <div style={styles.viewTabs}>
          <button
            style={{ ...styles.viewTabBtn, ...(view === 'login' ? styles.viewTabActive : {}) }}
            onClick={() => switchView('login')}
          >
            登录
          </button>
          <button
            style={{ ...styles.viewTabBtn, ...(view === 'register' ? styles.viewTabActive : {}) }}
            onClick={() => switchView('register')}
          >
            注册
          </button>
        </div>

        {/* ================= 登录视图 ================= */}
        {view === 'login' && (
          <>
            <div style={styles.tabs}>
              <button
                style={{ ...styles.tabBtn, ...(loginMode === 'code' ? styles.tabActive : {}) }}
                onClick={() => switchMode('code')}
              >
                验证码登录
              </button>
              <button
                style={{ ...styles.tabBtn, ...(loginMode === 'password' ? styles.tabActive : {}) }}
                onClick={() => switchMode('password')}
              >
                密码登录
              </button>
            </div>

            <form onSubmit={handleLogin} style={styles.form}>
              <div style={styles.field}>
                <label>{loginMode === 'password' ? '用户名' : '手机号/邮箱'}</label>
                <input
                  type="text"
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  placeholder={loginMode === 'password' ? '请输入用户名' : '请输入手机号或邮箱'}
                  disabled={isLoading}
                  style={inputStyle}
                />
              </div>

              {loginMode === 'password' ? (
                <div style={styles.field}>
                  <label>密码</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="不少于6位"
                    disabled={isLoading}
                    style={inputStyle}
                  />
                </div>
              ) : (
                <div style={styles.field}>
                  <label>验证码</label>
                  <div style={styles.codeRow}>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="请输入6位验证码"
                      disabled={isLoading}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={isLoading || countdown > 0}
                      style={{ ...styles.codeBtn, ...(countdown > 0 ? styles.codeBtnDisabled : {}) }}
                    >
                      {countdown > 0 ? `${countdown}s` : '获取验证码'}
                    </button>
                  </div>
                </div>
              )}

              {notice && <div style={styles.notice}>{notice}</div>}
              {error && <div style={styles.error}>{error}</div>}

              <button type="submit" disabled={isLoading} style={styles.button}>
                {isLoading ? '处理中...' : '登 录'}
              </button>

              {loginMode === 'password' && (
                <div style={styles.tip}>
                  演示账号: admin / 123456
                </div>
              )}
            </form>
          </>
        )}

        {/* ================= 注册视图 ================= */}
        {view === 'register' && (
          <>
            <div style={styles.tabs}>
              <button
                style={{ ...styles.tabBtn, ...(regMode === 'code' ? styles.tabActive : {}) }}
                onClick={() => switchRegMode('code')}
              >
                验证码注册
              </button>
              <button
                style={{ ...styles.tabBtn, ...(regMode === 'username' ? styles.tabActive : {}) }}
                onClick={() => switchRegMode('username')}
              >
                用户名注册
              </button>
            </div>

            <form onSubmit={handleRegister} style={styles.form}>
              <div style={styles.field}>
                <label>用户名</label>
                <input
                  type="text"
                  value={regUsername}
                  onChange={(e) => setRegUsername(e.target.value)}
                  placeholder="2-20位，支持中文/字母/数字"
                  disabled={isLoading}
                  style={inputStyle}
                />
              </div>

              {regMode === 'username' ? (
                <>
                  <div style={styles.field}>
                    <label>密码</label>
                    <input
                      type="password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="不少于6位"
                      disabled={isLoading}
                      style={inputStyle}
                    />
                  </div>
                  <div style={styles.field}>
                    <label>确认密码</label>
                    <input
                      type="password"
                      value={regConfirm}
                      onChange={(e) => setRegConfirm(e.target.value)}
                      placeholder="再次输入密码"
                      disabled={isLoading}
                      style={inputStyle}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.field}>
                    <label>手机号/邮箱</label>
                    <input
                      type="text"
                      value={regContact}
                      onChange={(e) => setRegContact(e.target.value)}
                      placeholder="用于接收验证码"
                      disabled={isLoading}
                      style={inputStyle}
                    />
                  </div>
                  <div style={styles.field}>
                    <label>验证码</label>
                    <div style={styles.codeRow}>
                      <input
                        type="text"
                        value={regCode}
                        onChange={(e) => setRegCode(e.target.value)}
                        placeholder="请输入6位验证码"
                        disabled={isLoading}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={isLoading || countdown > 0}
                        style={{ ...styles.codeBtn, ...(countdown > 0 ? styles.codeBtnDisabled : {}) }}
                      >
                        {countdown > 0 ? `${countdown}s` : '获取验证码'}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {notice && <div style={styles.notice}>{notice}</div>}
              {error && <div style={styles.error}>{error}</div>}

              <button type="submit" disabled={isLoading} style={styles.button}>
                {isLoading ? '处理中...' : '注 册'}
              </button>
              <div style={styles.tip}>注册成功后将自动登录</div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// 样式
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  modal: {
    background: '#fff',
    padding: '32px 28px',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '400px',
    position: 'relative',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    maxHeight: '92vh',
    overflowY: 'auto',
  },
  closeBtn: {
    position: 'absolute',
    top: '8px',
    right: '12px',
    background: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#999',
  },
  title: { textAlign: 'center', marginBottom: '10px', color: '#333' },
  viewTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '14px',
    justifyContent: 'center',
  },
  viewTabBtn: {
    padding: '6px 28px',
    border: '1px solid #d9d9d9',
    borderRadius: '20px',
    background: '#fafafa',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s',
    color: '#333',
  },
  viewTabActive: {
    background: '#1677ff',
    borderColor: '#1677ff',
    color: '#fff',
  },
  tabs: {
    display: 'flex',
    marginBottom: '18px',
    borderRadius: '4px',
    overflow: 'hidden',
    border: '1px solid #d9d9d9',
  },
  tabBtn: {
    flex: 1,
    padding: '8px 0',
    border: 'none',
    background: '#fafafa',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s',
    color: '#333',
  },
  tabActive: {
    background: '#1677ff',
    color: '#fff',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  input: {
    padding: '10px 12px',
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    fontSize: '14px',
    outline: 'none',
  },
  codeRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  codeBtn: {
    padding: '10px 16px',
    background: '#1677ff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  codeBtnDisabled: { background: '#d9d9d9', cursor: 'not-allowed' },
  notice: {
    color: '#1677ff',
    fontSize: '13px',
    background: '#e6f4ff',
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #91caff',
  },
  error: {
    color: '#ff4d4f',
    fontSize: '13px',
    background: '#fff2f0',
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #ffccc7',
  },
  button: {
    padding: '10px',
    background: '#1677ff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background 0.2s',
    marginTop: '4px',
  },
  tip: { textAlign: 'center', color: '#999', fontSize: '12px', marginTop: '10px' },
};

export default LoginModal;
